import React, { useState, useEffect } from "react";
import styles from "./attendance-details.module.css";
import { Select, Typography } from "antd";
import CustomTextfield from "../../../../../common/input/custom-textfield/custom-textfield";
import CustomSelect from "../../../../../common/input/custom-select/custom-select";
import CustomButton from "../../../../../common/custom-button/custom-button";
import CustomTable from "../../../../../common/custom-table/custom-table";
import SVGIcon from "../../../../../constants/SVGIcon";
import CustomDrawer from "../../../../../common/custom-drawer/custom-drawer";
import {
  getDeptById,
  getTeacherAttendanceDetails,
} from "../../../../../services/teachers";
import ViewDetailsTeachers from "../../drawers-content/components/view-details";
import { Controller, useForm } from "react-hook-form";
import { fetchSubjectTypes } from "../../../../../services/subject";

const TeacherAttendanceDetails = ({ selectedDate }) => {
  const [attendanceDetails, setAttendanceDetails] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [allDept, setAllDept] = useState([]);

  const { control, trigger, getValues, reset } = useForm({
    mode: "onChange",
    defaultValues: {
      level: null,
    },
  });

  const fetchDeptById = async (id) => {
    try {
      setLoading(true);
      const response = await getDeptById(id);
      if (response?.data) {
        const data = response?.data?.data;
        setAttendanceDetails(data);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeacherAttendanceDetails = async () => {
    try {
      setLoading(true);
      const params = { date: selectedDate };
      const response = await getTeacherAttendanceDetails(params);
      if (response?.data) {
        const data = response?.data?.data;
        setAttendanceDetails(data);
        return data;
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAllDept();
  }, []);

  useEffect(() => {
    fetchTeacherAttendanceDetails();
  }, [selectedDate]);

  const fetchAllDept = async () => {
    try {
      setLoading(true);
      const response = await fetchSubjectTypes();
      if (response?.data) {
        const deptData = response?.data?.data?.departments;
        setAllDept(deptData);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const searchedData = attendanceDetails.filter((item) => {
    const deptMatch = item?.department_name
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());

    const subjectMatch = item?.subjects?.some((sub) =>
      sub?.subject_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return deptMatch || subjectMatch;
  });

  const sumFun = (record, sumData) => {
    return record.subjects.reduce(
      (acc, item) => (acc += item[sumData] || 0),
      0
    );
  };

  const columns = [
    {
      title: "Department",
      dataIndex: "department_name",
      key: "department_name",
      sorter: (a, b) =>
        (a.department_name || "").localeCompare(b.department_name || ""),
    },
    {
      title: "Total Teachers",
      key: "totalTeachers",
      sorter: (a, b) =>
        sumFun(a, "total_teachers_count") - sumFun(b, "total_teachers_count"),
      render: (_, record) => {
        const total_teachers = sumFun(record, "total_teachers_count");
        return <span>{total_teachers}</span>;
      },
    },
    {
      title: "Total Male Teachers",
      key: "totalMaleTeachers",
      sorter: (a, b) =>
        sumFun(a, "male_present_count") - sumFun(b, "male_present_count"),
      render: (_, record) => {
        const total_male_present = sumFun(record, "male_present_count");
        const total_male_count = sumFun(record, "male_count");
        return `${total_male_present}/${total_male_count}`;
      },
    },
    {
      title: "Total Female Teachers",
      key: "totalFemaleTeachers",
      sorter: (a, b) =>
        sumFun(a, "female_present_count") - sumFun(b, "female_present_count"),
      render: (_, record) => {
        const total_female_present = sumFun(record, "female_present_count");
        const total_female_count = sumFun(record, "female_count");
        return `${total_female_present}/${total_female_count}`;
      },
    },
    {
      title: "Attendance",
      key: "attendance",
      sorter: (a, b) => {
        const aTotal =
          sumFun(a, "male_present_count") + sumFun(a, "female_present_count");
        const bTotal =
          sumFun(b, "male_present_count") + sumFun(b, "female_present_count");
        return aTotal - bTotal;
      },
      render: (_, record) => {
        const total_female_present = sumFun(record, "female_present_count");
        const total_male_present = sumFun(record, "male_present_count");
        const total_teachers = sumFun(record, "total_teachers_count");

        return `${total_female_present + total_male_present}/${total_teachers}`;
      },
    },
  ];

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setDrawerOpen(true);
  };

  const expandColumns = [
    {
      title: "Subjects",
      dataIndex: "subject_name",
      key: "subject_name_child",
      sorter: (a, b) =>
        (a.subject_name || "").localeCompare(b.subject_name || ""),
    },
    {
      title: "Total Teachers",
      dataIndex: "total_teachers_count",
      key: "total_teachers_count_child",
      sorter: (a, b) =>
        (a.total_teachers_count || 0) - (b.total_teachers_count || 0),
    },
    {
      title: "Male Teachers",
      key: "male_count_child",
      sorter: (a, b) =>
        (a.male_present_count || 0) - (b.male_present_count || 0),
      render: (_, record) =>
        `${record.male_present_count}/${record.male_count}`,
    },
    {
      title: "Female Teachers",
      key: "female_count_child",
      sorter: (a, b) =>
        (a.female_present_count || 0) - (b.female_present_count || 0),
      render: (_, record) =>
        `${record.female_present_count}/${record.female_count}`,
    },
    {
      title: "Attendance",
      key: "attendance_child",
      sorter: (a, b) => {
        const aPresent =
          (a.male_present_count || 0) + (a.female_present_count || 0);
        const bPresent =
          (b.male_present_count || 0) + (b.female_present_count || 0);
        return aPresent - bPresent;
      },
      render: (_, record) =>
        `${
          (record.male_present_count || 0) + (record.female_present_count || 0)
        }/${record.total_teachers_count || 0}`,
    },
    {
      title: "Action",
      key: "action_child",
      render: (_, record) => (
        <CustomButton
          variant="filled"
          type="primary"
          title="View Details"
          onClick={() => handleViewDetails(record)}
        />
      ),
    },
  ];

  const expandedRowRender = (record) => (
    <CustomTable
      columns={expandColumns}
      dataSource={record?.subjects || []}
      loading={loading}
      pagination={false}
    />
  );
  return (
    <>
      <div className={styles.main_container}>
        <div className={styles.title_container}>
          <Typography className={styles.title}>Attendance Details</Typography>
          <div className={styles.rightFieldContainer}>
            <div className={styles.filter_container}>
              <CustomTextfield
                id="search"
                type="text"
                className={styles.inputField}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                suffix={
                  <SVGIcon
                    name="search-icon"
                    width="24"
                    height="24"
                    className="anticon"
                    stroke="#667085"
                  />
                }
                placeholder="Search ..."
              />
            </div>

            <div className={styles.responsive_select}>
              <Controller
                name="dept"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomSelect
                    id="deptSelect"
                    labelColor="#171717"
                    placeholder="--Select--"
                    size="large"
                    value={value}
                    onChange={(val) => {
                      onChange(val);

                      if (val === "all") {
                        fetchTeacherAttendanceDetails();
                      } else {
                        fetchDeptById(val);
                      }
                    }}
                  >
                    <Select.Option key="all" value="all">
                      All Departments
                    </Select.Option>
                    {allDept?.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                  </CustomSelect>
                )}
              />
            </div>
          </div>
        </div>

        <CustomTable
          columns={columns}
          dataSource={searchedData.map((item, idx) => ({
            ...item,
            key: item.department_id || idx,
          }))}
          collapsible={true}
          pagination={false}
          renderCollapseContent={expandedRowRender}
          handleExpand={(expand, record) => {
            if (expand) {
            }
          }}
          loading={loading}
        />
      </div>
      {drawerOpen && (
        <CustomDrawer
          title="Details"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width="50vw"
          loading={loading}
          className={styles.drawer}
          drawerStyles={{ body: { padding: 0, margin: 0 } }}
          destroyOnClose
          footer={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <CustomButton
                variant="secondary"
                title={<Typography>Cancel</Typography>}
                disabled={loading}
                style={{ backgroundColor: "white" }}
                onClick={() => {
                  setDrawerOpen(false);
                }}
              />
            </div>
          }
        >
          <ViewDetailsTeachers teachers={selectedRecord.teachers} />
        </CustomDrawer>
      )}
    </>
  );
};

export default TeacherAttendanceDetails;
